import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router';
import { useMatrixParams } from '@/app/MatrixSwitch';
import type { ContactOption, UserDetail } from '@/core/models/practice.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { matrixPath } from '@/core/routing/matrix';
import { practiceService } from '@/core/services/practice';
import { Button } from '@/kit/Button';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { Accordion, ExpansionPanel } from '@/kit/ExpansionPanel';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { PageHeader } from '@/kit/PageHeader';
import { MatSelect } from '@/kit/MatSelect';
import { Select, SelectOption } from '@/kit/Select';
import { SideList } from '@/kit/SideList';
import { validateAddresses, validateForm, type AddressLine } from '../formRules';
import {
  CONTACT_HINT,
  CREDENTIALS_NOTICE,
  PASSWORD_HINT,
  USER_CONTROL_NAMES,
  USER_TYPE_HINT,
  blankAddress,
  buildUserPayload,
  controlErrors,
  errorFor,
  isUserFormValid,
  newUserValues,
  nextConfirmMismatch,
  userValuesFrom,
  type UserControlName,
  type UserFormValues,
} from './addUserRules';
import './AddUser.scss';

interface FormState {
  values: UserFormValues;
  /** `passwordMismatch` written onto the confirm control by the group validator (see nextConfirmMismatch). */
  confirmMismatch: boolean;
}

/** Angular `AddUserComponent`: create or edit a sign-in user (`/home/manage/addUser[;id=<base64 userId>]`). */
export function AddUser() {
  const navigate = useNavigate();
  const { id: encodedId } = useMatrixParams();

  const [form, setForm] = useState<FormState>(() => ({ values: newUserValues(), confirmMismatch: false }));
  const [dirty, setDirty] = useState<Set<UserControlName>>(new Set());
  const [touched, setTouched] = useState<Set<UserControlName>>(new Set());
  const [dataSource, setDataSource] = useState<AddressLine[]>(() => [blankAddress()]);
  const [contactOptions, setContactOptions] = useState<ContactOption[]>([]);
  const [isExpandAll, setIsExpandAll] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [isEdit, setIsEdit] = useState(false);
  /** True while a save is in flight, so a second click on Save / Save and Exit cannot submit twice. */
  const [saving, setSaving] = useState(false);

  const userId = useRef<string | null>(null);
  const mounted = useRef(false);

  const { values, confirmMismatch } = form;
  // setPasswordRequired(true) on add, (false) on edit.
  const passwordRequired = !isEdit;

  const messageFor = (name: UserControlName) =>
    errorFor(name, values, passwordRequired, confirmMismatch, dirty.has(name) || touched.has(name));

  const touch = (name: UserControlName) => setTouched((current) => new Set(current).add(name));

  /** Angular `ng-invalid ng-touched` on the control. */
  const isInvalid = (name: UserControlName) =>
    controlErrors(name, values, passwordRequired, confirmMismatch) !== null && touched.has(name);

  /** A control edited by the user (or patched by code): the group validator re-runs on every change. */
  const patch = (changes: Partial<UserFormValues>, changed: UserControlName, markDirty: boolean) => {
    setForm((current) => {
      const next = { ...current.values, ...changes };
      return { values: next, confirmMismatch: nextConfirmMismatch(current.confirmMismatch, next, changed) };
    });
    if (markDirty) setDirty((current) => new Set(current).add(changed));
  };

  const setValue = (name: UserControlName, value: string | null) => patch({ [name]: value }, name, true);

  const textProps = (name: 'firstName' | 'email' | 'phone' | 'password' | 'confirmPassword') => ({
    value: values[name] ?? '',
    onChange: (event: ChangeEvent<HTMLInputElement>) => setValue(name, event.target.value),
    onBlur: () => touch(name),
    invalid: isInvalid(name),
  });

  const selectClass = (name: UserControlName, base: string) =>
    `${base}${isInvalid(name) ? ' ng-invalid ng-touched' : ''}`;

  const resetForm = (next: UserFormValues) => {
    setForm({ values: next, confirmMismatch: nextConfirmMismatch(false, next, 'reset') });
    setDirty(new Set());
    setTouched(new Set());
  };

  const populate = (user: UserDetail) => {
    if (!mounted.current) return;
    setIsEdit(true);
    userId.current = user.id;
    resetForm(userValuesFrom(user));

    setDataSource(
      user.addresses.length > 0
        ? user.addresses.map((a) => ({
            addressType: String(a.addressType),
            addressLine: a.addressLine,
            city: a.city,
            county: a.county ?? '',
            country: a.country,
            postCode: a.postCode,
          }))
        : [blankAddress()],
    );
  };

  const load = (id: string) => {
    practiceService.getUser(id).then(populate, (error) => notifyError(error));
  };

  const startNew = () => {
    setIsEdit(false);
    userId.current = null;
    resetForm(newUserValues());
    setDataSource([blankAddress()]);
  };

  useEffect(() => {
    mounted.current = true;
    practiceService.getContactOptions().then(
      (options) => {
        if (mounted.current) setContactOptions(options);
      },
      (error) => notifyError(error),
    );
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (encodedId) {
      load(atob(encodedId));
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- the route param (paramMap subscription) drives the form
      startNew();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load/startNew only use refs and stable setters
  }, [encodedId]);

  const toggleAccordion = () => setIsExpandAll((current) => !current);

  /** ap5-a3: choosing a client contact fills in the name, email and phone already held for them. */
  const onContactChange = (contactId: string | null) => {
    const contact = contactOptions.find((option) => option.id === contactId);
    if (!contact) {
      return;
    }
    setForm((current) => {
      const next = {
        ...current.values,
        firstName: contact.name ?? current.values.firstName,
        email: contact.email ?? current.values.email,
        phone: contact.phone ?? current.values.phone,
      };
      return { values: next, confirmMismatch: nextConfirmMismatch(current.confirmMismatch, next, 'firstName') };
    });
  };

  const updateAddress = (index: number, changes: Partial<AddressLine>) =>
    setDataSource((rows) => rows.map((row, i) => (i === index ? { ...row, ...changes } : row)));

  const addAddress = () => setDataSource((rows) => [...rows, blankAddress()]);

  const removeAddress = (index: number) =>
    setDataSource((rows) => {
      const kept = rows.filter((_, i) => i !== index);
      return kept.length > 0 ? kept : [blankAddress()];
    });

  const onCancel = () => {
    if (isEdit && userId.current) {
      load(userId.current);
    } else {
      startNew();
    }
  };

  const onClose = () => void navigate('/home/manage/users');

  const save = (exitAfterSave: boolean) => {
    if (!validateForm(isUserFormValid(values, passwordRequired, confirmMismatch))) {
      setTouched(new Set(USER_CONTROL_NAMES));
      return;
    }

    // ap5-a3: the address rows are ngModel-bound, so they are checked here rather than by the form.
    const addressError = validateAddresses(dataSource);
    if (addressError) {
      notifyError(addressError);
      return;
    }

    const payload = buildUserPayload(userId.current, values, dataSource);

    setSaving(true);
    practiceService.saveUser(payload).then(
      (response) => {
        notifySuccess(response.message);
        if (exitAfterSave) {
          void navigate('/home/manage/users');
        } else if (!isEdit) {
          void navigate(matrixPath('/home/manage', 'addUser', { id: btoa(response.universalId) }));
        } else {
          load(response.universalId);
        }
      },
      (error) => notifyError(error),
    ).finally(() => {
      if (mounted.current) setSaving(false);
    });
  };

  const addressColumns: DataTableColumn<AddressLine>[] = [
    {
      id: 'name',
      header: '',
      headerClassName: 'w-100',
      cell: (element, index) => (
        <MatSelect
          className="w-100"
          placeholder="Main"
          panelClass="myPanelClass"
          value={element.addressType}
          options={[
            { value: '0', label: 'Main' },
            { value: '1', label: 'Office' },
          ]}
          onChange={(value) => updateAddress(index, { addressType: value })}
        />
      ),
    },
    {
      id: 'address',
      header: (
        <>
          Address<span className="text-red">*</span>
        </>
      ),
      headerClassName: 'wp-100',
      cell: (element, index) => (
        <InputField
          fieldClassName="wp-100"
          maxLength={500}
          value={element.addressLine}
          onChange={(event) => updateAddress(index, { addressLine: event.target.value })}
        />
      ),
    },
    {
      id: 'city',
      header: (
        <>
          City/Town<span className="text-red">*</span>
        </>
      ),
      headerClassName: 'w-100',
      cell: (element, index) => (
        <InputField
          fieldClassName="w-100"
          maxLength={100}
          charMode="text"
          value={element.city}
          onChange={(event) => updateAddress(index, { city: event.target.value })}
        />
      ),
    },
    {
      id: 'county',
      header: 'County',
      headerClassName: 'w-100',
      cell: (element, index) => (
        <InputField
          fieldClassName="w-100"
          maxLength={100}
          charMode="text"
          value={element.county}
          onChange={(event) => updateAddress(index, { county: event.target.value })}
        />
      ),
    },
    {
      id: 'country',
      header: (
        <>
          Country<span className="text-red">*</span>
        </>
      ),
      headerClassName: 'w-120',
      cell: (element, index) => (
        <InputField
          fieldClassName="w-120"
          charMode="text"
          value={element.country}
          onChange={(event) => updateAddress(index, { country: event.target.value })}
        />
      ),
    },
    {
      id: 'zip',
      header: (
        <>
          Post Code<span className="text-red">*</span>{' '}
        </>
      ),
      headerClassName: 'w-100',
      cell: (element, index) => (
        <InputField
          fieldClassName="w-100"
          maxLength={20}
          charMode="alphanumeric"
          value={element.postCode}
          onChange={(event) => updateAddress(index, { postCode: event.target.value })}
        />
      ),
    },
    {
      id: 'close',
      header: '',
      headerClassName: 'w-50',
      cell: (_element, index) => (
        <Button className="close-icn" title="Delete" onClick={() => removeAddress(index)}>
          <Icon name="close" outlined />
        </Button>
      ),
    },
  ];

  return (
    <div className="sidemenu-hide add-user-page">
      <div className="d-flex h-100">
        <SideList />
        <div className="form wp-100">
          <div>
            <div className="sidenav-details mb-4">
              {/* Design review 2 Nov 2026: Save / Save and Exit / Cancel Changes are page actions, so they sit top-right in the sticky
                  header; the footer bar is gone. */}
              <PageHeader title={isEdit ? 'Edit User' : 'Add New User'}>
                <Button className="action-button primary-button" busy={saving} onClick={() => save(false)}>
                  Save
                </Button>
                <Button className="action-button primary-button" busy={saving} onClick={() => save(true)}>
                  Save and Exit
                </Button>
                <Button className="action-button secondary-button" onClick={onCancel}>
                  Cancel Changes
                </Button>
                <Button className="close-btn" aria-label="Close" onClick={onClose}>
                  <Icon name="close" />
                </Button>
              </PageHeader>
              <div className="mt-1">
                <div className="accordion-toggle">
                  {isExpandAll && (
                    <Icon
                      name="keyboard_double_arrow_down"
                      onClick={() => {
                        toggleAccordion();
                        setPanelOpen(true);
                      }}
                    />
                  )}
                  {!isExpandAll && (
                    <Icon
                      name="keyboard_double_arrow_up"
                      onClick={() => {
                        toggleAccordion();
                        setPanelOpen(false);
                      }}
                    />
                  )}
                </div>
                <Accordion>
                  <ExpansionPanel title="Basic Info" expanded={panelOpen} onExpandedChange={setPanelOpen}>
                    <div className="sidenav-body p-1">
                      <form onSubmit={(event) => event.preventDefault()}>
                        <div className="sidenav-body">
                          <div className="sidenav-body-content p-0">
                            <div className="wp-100">
                              <div>
                                <div className="d-flex gap-30">
                                  <div className="form-field">
                                    <p>
                                      User Type<span className="text-red">*</span>
                                    </p>
                                    <Select
                                      className={selectClass('userType', 'w-260')}
                                      value={values.userType}
                                      onChange={(value) => setValue('userType', value as string | null)}
                                      onBlur={() => touch('userType')}
                                    >
                                      <SelectOption value="">Select User Type</SelectOption>
                                      <SelectOption value="admin">Admin</SelectOption>
                                      <SelectOption value="standard">Standard</SelectOption>
                                    </Select>
                                    <span className="font-size-12">{USER_TYPE_HINT}</span>
                                    {messageFor('userType') && (
                                      <span className="text-red font-size-12">{messageFor('userType')}</span>
                                    )}
                                  </div>
                                  <div className="form-field">
                                    <p>Client Contact</p>
                                    <Select<ContactOption>
                                      className={selectClass('contact', 'w-260')}
                                      items={contactOptions}
                                      bindLabel="name"
                                      bindValue="id"
                                      placeholder="Select a contact (optional)"
                                      clearable
                                      value={values.contact}
                                      onChange={(value, item) => {
                                        setValue('contact', value as string | null);
                                        onContactChange(item?.id ?? null);
                                      }}
                                      onBlur={() => touch('contact')}
                                    />
                                    <span className="font-size-12">{CONTACT_HINT}</span>
                                  </div>
                                </div>

                                <div className="form-field">
                                  <p>
                                    First Name <span className="text-red">*</span>
                                  </p>
                                  <div className="form-field-name">
                                    <Select
                                      className={selectClass('title', 'w-100 mr-10')}
                                      value={values.title}
                                      onChange={(value) => setValue('title', value as string | null)}
                                      onBlur={() => touch('title')}
                                    >
                                      <SelectOption value="Mr">Mr</SelectOption>
                                      <SelectOption value="Mrs">Mrs</SelectOption>
                                      <SelectOption value="Miss">Miss</SelectOption>
                                      <SelectOption value="Ms">Ms</SelectOption>
                                    </Select>
                                    <InputField
                                      fieldClassName="w-434"
                                      maxLength={255}
                                      charMode="text"
                                      {...textProps('firstName')}
                                    />
                                  </div>
                                  {messageFor('firstName') && (
                                    <span className="text-red font-size-12">{messageFor('firstName')}</span>
                                  )}
                                </div>

                                <div className="d-flex gap-30">
                                  <div className="form-field">
                                    <p>
                                      Email<span className="text-red">*</span>
                                    </p>
                                    <InputField
                                      fieldClassName="w-260"
                                      type="email"
                                      placeholder="name@example.co.uk"
                                      maxLength={255}
                                      {...textProps('email')}
                                    />
                                    {messageFor('email') && (
                                      <span className="text-red font-size-12">{messageFor('email')}</span>
                                    )}
                                  </div>
                                  <div className="form-field">
                                    <p>
                                      Phone<span className="text-red">*</span>
                                    </p>
                                    <InputField
                                      fieldClassName="w-260"
                                      placeholder="e.g. 020 7946 0000"
                                      maxLength={20}
                                      charMode="number"
                                      extraChars="+()- "
                                      {...textProps('phone')}
                                    />
                                    {messageFor('phone') && (
                                      <span className="text-red font-size-12">{messageFor('phone')}</span>
                                    )}
                                  </div>
                                </div>

                                {/* ap5-a3: maxlength was 8, which silently truncated longer passwords so the new user could
                                    never sign in with the password the administrator typed. */}
                                <div className="d-flex gap-30">
                                  <div className="form-field">
                                    <p>
                                      Password<span className="text-red">*</span>
                                    </p>
                                    <InputField
                                      fieldClassName="w-260"
                                      autoComplete="new-password"
                                      maxLength={128}
                                      type="password"
                                      {...textProps('password')}
                                    />
                                    {!messageFor('password') && <span className="font-size-12">{PASSWORD_HINT}</span>}
                                    {messageFor('password') && (
                                      <span className="text-red font-size-12">{messageFor('password')}</span>
                                    )}
                                  </div>
                                  <div className="form-field">
                                    <p>
                                      Confirm Password<span className="text-red">*</span>
                                    </p>
                                    <InputField
                                      fieldClassName="w-260"
                                      autoComplete="new-password"
                                      maxLength={128}
                                      type="password"
                                      {...textProps('confirmPassword')}
                                    />
                                    {messageFor('confirmPassword') && (
                                      <span className="text-red font-size-12">{messageFor('confirmPassword')}</span>
                                    )}
                                  </div>
                                </div>
                                {/* ap5-a3: the administrator is told that no activation email is sent. */}
                                {!isEdit && (
                                  <div className="mb-1">
                                    <span className="font-size-14">{CREDENTIALS_NOTICE}</span>
                                  </div>
                                )}
                                <div className="content-box hr-top">
                                  <div className="form-field-title mt-1 mb-1">
                                    <p>Address Details</p>
                                    <span className="font-size-14"> </span>
                                  </div>
                                  <div className="sidenav-table sidenav-table-input wp-100">
                                    <div className="mat-table-wrapper contact-detail-table">
                                      <DataTable
                                        columns={addressColumns}
                                        rows={dataSource}
                                        rowKey={(_row, index) => index}
                                        stickyHeader
                                      />
                                    </div>
                                  </div>
                                  <div className="mb-10 mt-10">
                                    <a
                                      href="#"
                                      className="link hyperlink"
                                      onClick={(event) => {
                                        event.preventDefault();
                                        addAddress();
                                      }}
                                    >
                                      Add New Address
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </ExpansionPanel>
                </Accordion>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
